import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { withBackend } from '../utils/api';

// Odoo mirror of the HubSpot deal picker: pick an opportunity or a project →
// its context (client, value, stage, tasks, notes) lands in the prompt and
// the standard generation flow builds a personalized deck or report around it.
export default function OdooRecordPickerModal({ userId, onPicked, onClose }) {
  const [records, setRecords] = useState([]);
  const [kind, setKind] = useState('opportunity'); // 'opportunity' | 'project'
  const [loading, setLoading] = useState(true);
  const [fetchingId, setFetchingId] = useState(null);
  const [query, setQuery] = useState('');
  const searchTimer = useRef(null);

  const load = async (k, q = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ user_id: userId, kind: k });
      if (q) params.set('q', q);
      const res = await fetch(withBackend(`/api/odoo/records?${params}`));
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load records');
      setRecords(data.records || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load records');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Cancel any pending debounced search armed under the previous kind —
    // otherwise a stale fetch can race this one and win setRecords.
    clearTimeout(searchTimer.current);
    load(kind, query.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = (value) => {
    setQuery(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(kind, value.trim()), 400);
  };

  const pick = async (record) => {
    setFetchingId(record.id);
    try {
      const res = await fetch(withBackend('/api/odoo/record-context'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, kind, record_id: record.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load record details');
      onPicked?.(data.context_text, data.record);
      onClose?.();
    } catch (err) {
      toast.error(err.message || 'Failed to load record details');
    } finally {
      setFetchingId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Pick an Odoo record"
    >
      <div
        className="flex max-h-[70vh] w-full max-w-lg flex-col rounded-2xl border border-border bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-border/60 px-5 py-4">
          <h2 className="text-base font-semibold text-foreground">Create from Odoo</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="border-b border-border/60 px-5 py-3 space-y-2.5">
          <div className="flex gap-1 rounded-lg bg-muted p-0.5 w-fit">
            {[['opportunity', 'Opportunities'], ['project', 'Projects']].map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  kind === k ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <input
              type="text"
              value={query}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={kind === 'project' ? 'Search projects…' : 'Search opportunities…'}
              className="min-w-0 flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading…
            </div>
          ) : records.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {query
                ? 'Nothing matches that search.'
                : kind === 'project' ? 'No projects found in your Odoo database.' : 'No opportunities found in your Odoo database.'}
            </p>
          ) : (
            <div className="space-y-1">
              {records.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => pick(r)}
                  disabled={fetchingId !== null}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-muted/60 disabled:opacity-60"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{r.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {[r.subtitle, r.meta].filter(Boolean).join(' · ') || '—'}
                    </p>
                  </div>
                  {fetchingId === r.id && (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" aria-hidden="true" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <p className="border-t border-border/60 px-5 py-3 text-[11px] text-muted-foreground">
          The record's client, value, stage, tasks, and notes are added to your prompt —
          then the normal generation flow takes over.
        </p>
      </div>
    </div>
  );
}
